import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Portfolio', path: '/portfolio' },
  { name: 'Knowledge Hub', path: '/knowledge-hub' },
  { name: 'Contact', path: '/contact' },
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleHashLinkClick = (e, path) => {
    e.preventDefault();
    const [pathname, hash] = path.split('#');
    
    if (location.pathname === pathname && hash) {
      const element = document.getElementById(hash);
      if (element) {
        const yOffset = -100;
        const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
      setIsOpen(false);
    } else {
      setIsOpen(false);
      navigate(path);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'glass py-4' : 'bg-transparent py-6'
        }`}
        data-testid="main-navigation"
      >
        <div className="arch-container">
          <nav className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="relative z-50" data-testid="logo-link">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="flex items-center"
              >
                <img 
                  src="/av-logo-48.png" 
                  alt="Agastya Ventures" 
                  className="h-10 md:h-12 w-auto"
                />
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={(e) => link.path.includes('#') ? handleHashLinkClick(e, link.path) : undefined}
                  className={`text-sm uppercase tracking-widest font-medium transition-colors duration-300 line-hover ${
                    location.pathname === link.path
                      ? 'text-gold'
                      : 'text-white/70 hover:text-white'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <a
                href="tel:+917977482624"
                className="flex items-center gap-2 text-white/70 hover:text-gold transition-colors"
                data-testid="phone-link"
              >
                <Phone className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">+91 79774 82624</span>
              </a>
              <Link to="/contact">
                <Button 
                  className="bg-gold text-black hover:bg-gold-light rounded-none px-6 py-5 text-xs uppercase tracking-widest font-bold"
                  data-testid="header-cta-button"
                >
                  Get Consultation
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden relative z-50 p-2"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? (
                <X className="w-6 h-6 text-white" />
              ) : (
                <Menu className="w-6 h-6 text-white" />
              )}
            </button>
          </nav>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-background" />
            <motion.nav
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="relative h-full flex flex-col items-center justify-center gap-8"
            >
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.05 }}
                >
                  <Link
                    to={link.path}
                    onClick={(e) => link.path.includes('#') ? handleHashLinkClick(e, link.path) : undefined}
                    className={`text-2xl font-serif transition-colors ${
                      location.pathname === link.path
                        ? 'text-gold'
                        : 'text-white hover:text-gold'
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className="mt-8 flex flex-col items-center gap-4"
              >
                <a
                  href="tel:+917977482624"
                  className="flex items-center gap-2 text-white/70 hover:text-gold transition-colors"
                >
                  <Phone className="w-5 h-5" strokeWidth={1.5} />
                  <span>+91 79774 82624</span>
                </a>
                <a
                  href="mailto:info@agastyaventures.com"
                  className="flex items-center gap-2 text-white/70 hover:text-gold transition-colors"
                >
                  <Mail className="w-5 h-5" strokeWidth={1.5} />
                  <span>info@agastyaventures.com</span>
                </a>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
